import { useState, Suspense } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import SplashScreen from "./components/splash/SplashAppleBubble";
import InstallPWAPrompt from "./components/auth/InstallPWAPrompt";
import { OfflineBanner } from "./components/common/OnlineStatusIndicator";
import { Toaster } from "sonner";
import ScrollToTop from "./components/layout/ScrollToTop";
import AppUpdateNotification from "./components/notifications/AppUpdateNotification";
import WelcomeAnimation from "./components/auth/WelcomeAnimation";
import BiometricSetupPrompt from "./components/auth/BiometricSetupPrompt";
import AppInstallReporter from "./components/auth/AppInstallReporter";
import ForcePasswordChangeDialog from "./components/auth/ForcePasswordChangeDialog";
import DownloadProgressOverlay from "./components/common/DownloadProgressOverlay";
import SuperadminUnlockScreen from "./components/auth/SuperadminUnlockScreen";
import { PageLoader } from "./config/api";
import { useAuth } from "./hooks/useAuth";
import { useModuleAccess, usePublicModules } from "./hooks/useModuleAccess";
import AppRoutes from "./routes/AppRoutes";

// Re-export API utilities for backward compatibility
// All existing imports like `import { axiosInstance } from "../App"` continue to work
export { API, axiosInstance, getCachedData, setCachedData, clearCache, getApiErrorMessage } from "./config/api";

function App() {
  const { user, loading, handleLogin, handleLogout, handleUserUpdate } = useAuth();
  const { isModuleOn } = useModuleAccess(user?.role);
  // Module kosmetik (welcome animation/confetti) berkuatkuasa SEMUA user termasuk superadmin
  const { isPublicModuleOn, loading: publicModulesLoading } = usePublicModules();
  
  const [showSplash, setShowSplash] = useState(() => {
    const lastSplashTime = localStorage.getItem('lastSplashTime');
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;
    
    if (lastSplashTime && (now - parseInt(lastSplashTime, 10)) < FIVE_MINUTES) {
      return false;
    }
    
    const splashShownThisSession = sessionStorage.getItem('splashShown');
    if (splashShownThisSession) {
      return false;
    }
    
    const isResetPasswordPage = window.location.pathname === '/reset-password';
    const isSetupPasswordPage = window.location.pathname === '/setup-password';
    return !isResetPasswordPage && !isSetupPasswordPage;
  });
  
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);

  const handleSplashComplete = () => {
    localStorage.setItem('lastSplashTime', Date.now().toString());
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  const onLogin = (userData, token) => {
    const mustChange = handleLogin(userData, token);
    if (mustChange) {
      setShowForcePasswordChange(true);
    }
  };

  const onPasswordChanged = (updatedUser) => {
    handleUserUpdate(updatedUser);
    setShowForcePasswordChange(false);
  };

  if (loading && !showSplash) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-pink-50 via-blue-50 to-purple-50">
        <div className="text-lg opacity-0">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <OfflineBanner />
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{ style: { marginTop: '60px' } }}
      />
      <BrowserRouter>
        <ScrollToTop />
        <AppUpdateNotification user={user} />
        {user?.role === 'superadmin' && user?.superadmin_locked ? (
          <SuperadminUnlockScreen
            user={user}
            onUnlocked={() => handleUserUpdate({ ...user, superadmin_locked: false })}
            onLogout={handleLogout}
          />
        ) : (
        <Suspense fallback={<PageLoader />}>
          <AppRoutes
            user={user}
            onLogin={onLogin}
            onLogout={handleLogout}
            onUserUpdate={handleUserUpdate}
          />
        </Suspense>
        )}
        {!publicModulesLoading && isPublicModuleOn('install_pwa_prompt') && <InstallPWAPrompt />}
        <AppInstallReporter user={user} />
        {/* Tunggu status module awam siap dimuatkan dahulu - elak animasi terpapar
            seketika sebelum status ON/OFF diketahui (isu muatan pertama).
            Berkuatkuasa untuk SEMUA user termasuk superadmin. */}
        {user && !publicModulesLoading && isPublicModuleOn('welcome_animation') && <WelcomeAnimation key={`welcome-${user.id}`} user={user} showConfetti={isPublicModuleOn('welcome_confetti')} />}
        {user && !showForcePasswordChange && <BiometricSetupPrompt user={user} />}
      </BrowserRouter>
      
      <ForcePasswordChangeDialog
        open={showForcePasswordChange}
        user={user}
        onPasswordChanged={onPasswordChanged}
      />
      {/* Bar kemajuan muat turun global (PDF/Excel/JSON) */}
      <DownloadProgressOverlay />
    </div>
  );
}

export default App;
